import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/damages")({
  component: DamagesPage,
});

import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import {
  useFetchDamages,
  useDamageMutations,
} from "@/hooks/api/inventory/useStockOps";
import {
  useFetchWarehouses,
  useFetchItems,
  useFetchUnits,
  ALL_ACTIVE_PARAMS,
} from "@/hooks/api/inventory/useCatalog";
import { DamageTable } from "@/components/inventory/tables/DamageTable";
import { DamageFormSheet } from "@/components/inventory/forms/DamageFormSheet";
import { DamageDetailSheet } from "@/components/inventory/forms/DetailViewSheet";
import type { DamageRecordPayload, DamageRecord } from "@/types/inventory";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function DamagesPage() {
  const navigate = useNavigate();
  const { data: capabilities } = useMyPermissions();
  // DamageRecordViewSet has no required_permission_map — list/retrieve fall
  // to read_permission (inventory.stock.view), create to write_permission
  // (inventory.stock.adjust). It has no update/destroy/other actions.
  const canView = can(capabilities, "inventory.stock.view");
  const canManage = can(capabilities, "inventory.stock.adjust");

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [showFilters, setShowFilters] = useState(false);
  const [warehouseFilter, setWarehouseFilter] = useState<string | undefined>(
    undefined
  );
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [viewTarget, setViewTarget] = useState<DamageRecord | null>(null);

  const damagesQuery = useFetchDamages(
    {
      page_size: pageSize,
      page,
      warehouse: warehouseFilter ? Number(warehouseFilter) : undefined,
    },
    { enabled: canView },
  );
  const damages = damagesQuery.data?.results ?? [];
  const count = damagesQuery.data?.count ?? 0;

  const { createDamage } = useDamageMutations();
  const warehousesQuery = useFetchWarehouses(ALL_ACTIVE_PARAMS);
  const warehouses = warehousesQuery.data?.results ?? [];
  const itemsQuery = useFetchItems(ALL_ACTIVE_PARAMS);
  const items = itemsQuery.data?.results ?? [];
  const unitsQuery = useFetchUnits(ALL_ACTIVE_PARAMS);
  const units = unitsQuery.data?.results ?? [];

  const isMutating = createDamage.isPending;
  const isLoading = (canView && damagesQuery.isPending) || isMutating;
  const isFetching = (canView && damagesQuery.isFetching) || isMutating;

  const resetFilters = () => {
    setWarehouseFilter(undefined);
    setPage(1);
  };

  const handleSubmit = async (data: DamageRecordPayload) => {
    await createDamage.mutateAsync(data);
    toast.success("Damage report submitted");
    setIsSheetOpen(false);
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Damage Reports"
          description="Record damaged or spoiled inventory items"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={canManage ? () => setIsSheetOpen(true) : undefined}
          addLabel="Report Damage"
          isLoading={isFetching}
        />

        {canView && (
        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SelectFilter
            placeholder="Warehouse"
            options={warehouses.map((w) => ({
              label: w.name,
              value: String(w.id),
            }))}
            value={warehouseFilter}
            onChange={(v) => {
              setWarehouseFilter(v);
              setPage(1);
            }}
          />
        </FiltersPanel>
        )}

        {canView && (
        <DamageTable
          damages={damages}
          isLoading={isLoading}
          onView={(d) => setViewTarget(d)}
          onRowClick={(d) =>
            navigate({
              to: "/admin/inventory/damages/$id",
              params: { id: String(d.id) },
            })
          }
        />
        )}
        {canView && (
        <PaginationComponent
          count={count}
          page={page}
          onPageChange={(p) => setPage(p)}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
        )}

        <DamageFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleSubmit}
          warehouses={warehouses}
          items={items}
          units={units}
          isLoading={isMutating}
        />

        {viewTarget && (
          <DamageDetailSheet
            isOpen={!!viewTarget}
            onClose={() => setViewTarget(null)}
            record={viewTarget}
          />
        )}
      </div>
    </SidebarInset>
  );
}
